import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── CommunitiesDropdown ───────────────────────────────────────────────────────
// Shown in the navbar when the user has 2+ active memberships.
const CommunitiesDropdown = ({ memberships }) => {
  const location   = useLocation();
  const [open, setOpen] = useState(false);
  const ref        = useRef(null);

  // Derive which community slug is currently active from the URL
  const tenantMatch = location.pathname.match(/^\/t\/([^/]+)/);
  const activeSlug  = tenantMatch ? tenantMatch[1] : null;

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const activeMembership = memberships.find((m) => {
    const slug = m.tenantId?.slug || m.tenantSlug;
    return slug === activeSlug;
  });
  const label = activeMembership
    ? (activeMembership.tenantId?.branding?.displayName || activeMembership.tenantId?.name || activeMembership.tenantName || activeSlug)
    : 'My Communities';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          font: 'inherit', color: 'inherit', padding: '0 4px',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {label}
        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 220, zIndex: 999,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '6px 14px 4px', fontSize: '0.7rem', fontWeight: 700,
            color: '#94a3b8', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Switch Community
          </div>
          {memberships.map((m) => {
            const slug    = m.tenantId?.slug || m.tenantSlug;
            const name    = m.tenantId?.branding?.displayName || m.tenantId?.name || m.tenantName || slug;
            const color   = m.tenantId?.branding?.primaryColor || '#4285F4';
            const isActive = slug === activeSlug;
            return (
              <Link
                key={m._id || slug}
                to={`/t/${slug}/`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', textDecoration: 'none',
                  color: isActive ? color : '#1e293b',
                  background: isActive ? color + '11' : 'transparent',
                  fontWeight: isActive ? 700 : 400, fontSize: '0.9rem',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: color, flexShrink: 0, opacity: isActive ? 1 : 0.4,
                }} />
                {name}
              </Link>
            );
          })}
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '6px 14px 8px' }}>
            <Link to="/dashboard" style={{ fontSize: '0.8rem', color: '#64748b', textDecoration: 'none' }}>
              ← All communities
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, panel, activeTenant, memberships, logout } = useAuth();
  const navigate = useNavigate();

  // Determine brand link destination:
  // - If tenant has websiteUrl configured, link externally to it
  // - Otherwise, link to CIP home
  const brandHref = activeTenant?.branding?.websiteUrl || '/';
  const isExternalLink = !!activeTenant?.branding?.websiteUrl;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Platform staff/admin — navigation lives in the shell sidenav, not the navbar.
  // Show only brand + logout so there's no duplication.
  const isPlatformPanel = panel === 'admin' || panel === 'staff';

  return (
    <nav className="navbar" data-panel={panel}>
      {isExternalLink ? (
        <a href={brandHref} className="navbar-brand" title="Go to main website">
          {activeTenant?.branding?.logoUrl && (
            <img 
              src={activeTenant.branding.logoUrl} 
              alt={activeTenant.branding.displayName || 'Logo'} 
              className="navbar-logo"
            />
          )}
          <span className="navbar-brand-text">
            {activeTenant?.branding?.displayName || 'Civic Intelligence Platform'}
          </span>
        </a>
      ) : (
        <Link to={brandHref} className="navbar-brand">
          {activeTenant?.branding?.logoUrl && (
            <img 
              src={activeTenant.branding.logoUrl} 
              alt={activeTenant.branding.displayName || 'Logo'} 
              className="navbar-logo"
            />
          )}
          <span className="navbar-brand-text">
            {activeTenant?.branding?.displayName || 'Civic Intelligence Platform'}
          </span>
        </Link>
      )}
      <div className="navbar-links">
        {user ? (
          <>
            {['admin', 'staff', 'community'].includes(panel) && (
              <Link to="/coms">Coms</Link>
            )}
            {!isPlatformPanel && (
              <>
                {/* Multi-membership: show community switcher dropdown; otherwise plain Dashboard link */}
                {(panel === 'user' || panel === 'community') && memberships?.length > 1
                  ? <CommunitiesDropdown memberships={memberships} />
                  : <Link to="/dashboard">Dashboard</Link>
                }
                {(panel === 'user' || panel === 'community') && (
                  <Link to="/membership">Membership</Link>
                )}
                {panel === 'community' && (
                  <Link to="/admin">Admin</Link>
                )}
              </>
            )}
            <button onClick={handleLogout}>Logout</button>
          </>
        ) : (
          <>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
